<script setup>

import { storeToRefs } from 'pinia';
import { useWindowStore } from '@/stores/window';
const { innerWidth } = storeToRefs(useWindowStore());
const shardPng = '/img/2026/boundless-growth-left-bottom-shard-desktop.png';

const props = defineProps({
    headline: {
        type: String,
        required: false,
    },
    downloadLink: {
        type: Object,
        required: false,
    },
    iconTitleContentCards: {
        type: Array,
        required: false,
    },
    bottomText: {
        type: String,
        required: false,
    },
    button: {
        type: Object,
        required: false,
    },
    image: {
        type: Object,
        require: false
    },
    attributes: {
        type: Object,
        default: () => ({}),
    },
});

const isMobile = computed(() => {
    return import.meta.client ? innerWidth.value < 1024 : false
})

const listItems = useTemplateRef('listItems');
const visible = ref(new Set());

onMounted(() => {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                visible.value = new Set([...visible.value, entry.target.dataset.itemIndex]);
                observer.unobserve(entry.target);
            }
        });
    }, { threshold: 0.15 });

    listItems.value?.forEach((el) => observer.observe(el));
});

</script>

<template>
    <section class="block-icon-title-content relative overflow-hidden pt-[60px] pb-[60px] lg:pt-[75px] lg:pb-0 text-white" tabindex="-1"
        style="background: linear-gradient(127deg, var(--magenta, #46062B) 13%, var(--purple, #820CA7) 34%, #a53646 95%)">
        <img :src="shardPng" loading="lazy" decoding="async" alt="" aria-hidden="true"
            class="hidden lg:display-block absolute bottom-0 left-0 pointer-events-none z-[0]" />
        <div class="relative z-[1] px-(--box-padding-lr-m) lg:px-(--box-padding-lr)">
            <div class="flex-container flex flex-col w-full py-0 lg:py-(--box-padding-tb) lg:flex-row lg:gap-16 lg:items-start">
                <div class="col col-1 flex flex-col lg:flex-[0_0_35%] lg:w-[35%]">
                    <h2 class="font-hubspot-serif font-normal text-white text-[34px] leading-[1.1] tracking-normal mb-(--box-padding-tb-m) md:text-[calc(60/var(--base-fs)*1rem)] lg:mb-(--box-padding-tb)" v-html="headline" v-if='headline'></h2>
                    <div class='desktop' v-if="(downloadLink?.button?.url && downloadLink?.button?.label) || (button?.url && button?.label)">
                        <div class='buttons flex flex-col gap-3 mb-[var(--box-padding-tb)]'>
                            <MessLink v-if="downloadLink.button && downloadLink.button.url && downloadLink.button.label" :href="downloadLink.button.url" :label="downloadLink.button.title"
                                class="inline-flex items-center gap-2.5 px-5 py-2.5 bg-white text-(--magenta) no-underline rounded-[50px] font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px] hover:bg-(--orange) hover:text-white transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
                                {{ downloadLink.button.label }}
                            </MessLink>
                            <MessLink v-if="button.url && button.label" :href="button.url" :label="button.title"
                                class="inline-flex items-center gap-2.5 px-5 py-2.5 bg-white text-(--magenta) no-underline rounded-[50px] font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px] hover:bg-(--orange) hover:text-white transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
                                {{ button.label }}
                            </MessLink>
                        </div>
                    </div>
                </div>
                <div class="col col-2 lg:flex-1">
                    <ul class="list-items desktop" :inert="isMobile">
                        <li v-for="(item, index) in iconTitleContentCards" :key="index"
                            ref="listItems" :data-item-index="index"
                            class="itc-reveal flex gap-9 py-10 border-b border-white/50 last:border-b-0"
                            :class="{ 'is-visible': visible.has(String(index)) }"
                            :style="{ transitionDelay: `${index * 150}ms` }">
                            <h3 v-html="item.headline" class="font-hubspot-serif flex-[0_0_35%] text-[calc(36/var(--base-fs)*1rem)] font-normal leading-[1.2] tracking-normal text-white mt-[-0.4375rem]"></h3>
                            <MessHtml v-if="item" tag="div" class='copy flex-1 font-hubspot-sans' :html="item.copy" />
                        </li>
                    </ul>
                    <ProjectFaq :items="iconTitleContentCards" :title='null' class="mobile" headlineTag="h3" headlineClass="h3" :inert="!isMobile">
                        <template #item="item">
                            <MessHtml v-if="item" tag="div" class='copy' :html="item[0]" />
                        </template>
                    </ProjectFaq>
                    <div class='mobile' v-if="(downloadLink?.button?.url && downloadLink?.button?.label) || (button?.url && button?.label)">
                        <div class='buttons flex flex-col gap-3 mb-[var(--box-padding-tb)]'>
                            <MessLink v-if="downloadLink.button && downloadLink.button.url && downloadLink.button.label" :href="downloadLink.button.url" :label="downloadLink.button.title"
                                class="inline-flex items-center gap-2.5 px-5 py-2.5 bg-white text-(--magenta) no-underline rounded-[50px] font-hubspot-serif text-base leading-[1.3] tracking-[0.32px] hover:bg-(--orange) hover:text-white transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
                                {{ downloadLink.button.label }}
                            </MessLink>
                            <MessLink v-if="button.url && button.label" :href="button.url" :label="button.title"
                                class="inline-flex items-center gap-2.5 px-5 py-2.5 bg-white text-(--magenta) no-underline rounded-[50px] font-hubspot-serif text-base leading-[1.3] tracking-[0.32px] hover:bg-(--orange) hover:text-white transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
                                {{ button.label }}
                            </MessLink>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </section>
</template>

<style>
.itc-reveal {
    opacity: 0;
    transform: translateX(-30px);
    transition: opacity 500ms ease, transform 500ms ease;
}

.itc-reveal.is-visible {
    opacity: 1;
    transform: translateX(0);
}

@media (prefers-reduced-motion: reduce) {
    .itc-reveal {
        transition: none;
        opacity: 1;
        transform: none;
    }
}

/* [child] targets richtext content rendered by MessHtml inside list items */
.block-icon-title-content .list-items li .copy * {
    font-family: var(--font-hubspot-sans);
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 0.32px;
    font-weight: 300;
    color: white;
}

/* [child] targets <p> elements rendered by MessHtml */
.block-icon-title-content .copy p {
    font-family: var(--font-hubspot-sans);
    font-size: 16px;
    line-height: 26px;
    font-weight: 300;
    letter-spacing: 0.32px;
    color: white;
}

/* [state] hover media query with compound :hover/:focus on child <a> elements */
@media (hover: hover) and (pointer: fine) {
    .block-icon-title-content .buttons a:hover,
    .block-icon-title-content .buttons a:focus {
        color: white;
    }
}

/* [state] :not(:last-child) pseudo-class selector on child <a> elements */
.block-icon-title-content .buttons a:not(:last-child) {
    margin-bottom: var(--box-padding-tb-m);
}

/* [child] targets mess-image element inside child component */
.block-icon-title-content .col-1 .mess-image {
    border-radius: var(--radius);
    overflow: hidden;
}

/* [child] targets .headline element inside ProjectFaq */
.block-icon-title-content .headline {
    color: white;
    font-family: var(--font-hubspot-serif);
    font-size: calc(36 / var(--base-fs) * 1rem);
    line-height: 1.2;
    font-weight: 400;
    letter-spacing: 0;
}

/* [child] targets mess-accordion (ProjectFaq) child component elements we don't own */
.block-icon-title-content .mess-accordion {
    border-top: 1px solid rgba(255, 255, 255, 0.50);
    padding: 1.5rem 0 0;
    margin: 0;
}

@media (min-width: 1024px) {
    .block-icon-title-content .mess-accordion {
        padding: 1.5rem 0;
    }
}

@media (min-width: 1024px) {
    .block-icon-title-content .mess-accordion {
        margin-bottom: 2.125rem;
    }
}

.block-icon-title-content .mess-accordion h3 {
    color: white;
    font-family: var(--font-hubspot-serif);
    font-size: calc(36 / var(--base-fs) * 1rem);
    line-height: 1.2;
    font-weight: 400;
    letter-spacing: 0;
}

.block-icon-title-content .mess-accordion .copy {
    margin-top: 1rem;
    color: var(--lavender);
    font-size: calc(17 / var(--base-fs) * 1rem);
    letter-spacing: 0.02125rem;
    font-weight: 300;
    line-height: 1.75rem;
}

.block-icon-title-content .mess-accordion .copy * {
    line-height: 1.75rem;
}

/* [state] :not(:last-child) pseudo-class on richtext <p> elements */
.block-icon-title-content .mess-accordion .copy p:not(:last-child) {
    margin-bottom: 1em;
}

.block-icon-title-content .mess-accordion .copy li {
    margin-bottom: 1em;
}

.block-icon-title-content .mess-accordion .copy a {
    color: var(--cream);
    border-bottom: 1px solid var(--cream);
}

/* [state] adjacent sibling combinator for accordion item spacing */
.block-icon-title-content .mess-accordion .accordion-item + .accordion-item {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-color: rgba(255, 255, 255, 0.50);
}

/* [state] compound adjacent sibling + :last-child selector */
.block-icon-title-content .mess-accordion .accordion-item + .accordion-item:last-child {
    padding-bottom: 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.50);
}

/* [state] hover media query with :focus/:hover on child component button */
@media (hover: hover) and (pointer: fine) {
    .block-icon-title-content .mess-accordion .accordion-item button:focus,
    .block-icon-title-content .mess-accordion .accordion-item button:hover {
        text-decoration: none;
    }
}

/* [child] toggle-icon is now an SVG circle +/- control */
.block-icon-title-content .mess-accordion .toggle-icon {
    margin-left: auto;
}

/* Mobile: match 2026 FAQ block styles */
@media (max-width: 1023px) {
    .block-icon-title-content .headline {
        font-size: calc(34 / var(--base-fs) * 1rem);
        line-height: 1.1;
    }

    .block-icon-title-content .mess-accordion h3 {
        font-size: calc(26 / var(--base-fs) * 1rem);
        line-height: 1.1;
    }

    .block-icon-title-content .mess-accordion .copy {
        color: white;
        font-family: var(--font-hubspot-sans);
        font-size: calc(16 / var(--base-fs) * 1rem);
        line-height: 26px;
        letter-spacing: 0.32px;
    }

    .block-icon-title-content .mess-accordion .toggle-icon {
        width: 32px;
        height: 32px;
        min-width: 32px;
    }

    .block-icon-title-content .mess-accordion .toggle-icon svg {
        width: 32px;
        height: 32px;
    }
}

</style>
